class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close('escape_key', 'cancel');
    });
    this.addEventListener('click', (event) => this.trackDiscountAction(event));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', () => {
      this.close('backdrop', 'cancel');
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const wasOpen = this.getAttribute('aria-hidden') === 'false';
    this.setAttribute('aria-hidden', 'false');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    if (!wasOpen) {
      window.TorrasAnalytics?.trackInteraction(this, {
        componentId: 'cart_drawer_shell',
        elementType: 'component',
        elementId: triggeredBy?.dataset?.analyticsElementId || 'add_to_cart_result',
        interactionName: 'open',
        interactionResult: 'success',
      });
    }
  }

  close(analyticsElementId = 'programmatic_close', analyticsResult = 'cancel') {
    const wasOpen = this.getAttribute('aria-hidden') === 'false';
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (wasOpen) {
      window.TorrasAnalytics?.trackInteraction(this, {
        componentId: 'cart_drawer_shell',
        elementType: 'component',
        elementId: analyticsElementId,
        interactionName: 'close',
        interactionResult: analyticsResult,
      });
    }
  }

  trackDiscountAction(event) {
    const action = event.target?.closest?.('[data-welcome-discount-cart-action]');
    if (!action || !action.closest('[data-cart-discount-analytics]')) return;
    const actionId = String(action.dataset.welcomeDiscountCartAction || 'action')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '_');
    window.TorrasAnalytics?.trackClick(action, {
      componentId: 'cart_drawer_discount_offer',
      elementType: 'button',
      elementId: `discount_${actionId}`,
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    const sectionsToRender = this.getSectionsToRender();
    const missingSectionIds = sectionsToRender
      .map((section) => section.id)
      .filter((sectionId) => !parsedState?.sections?.[sectionId]);

    if (missingSectionIds.length > 0) {
      try {
        const recoveredSections = await this.fetchSections(missingSectionIds);
        parsedState = {
          ...parsedState,
          sections: {
            ...(parsedState?.sections || {}),
            ...recoveredSections,
          },
        };
      } catch (error) {
        console.error('Unable to refresh the cart drawer sections.', error);
        this.redirectToCart();
        return;
      }
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState?.id;
    for (const section of sectionsToRender) {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) continue;
      const sectionInnerHTML = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (sectionInnerHTML == null) {
        this.redirectToCart();
        return;
      }
      sectionElement.innerHTML = sectionInnerHTML;
    }

    const itemCount = this.getCartItemCount(parsedState);
    if (
      itemCount != null &&
      window.Torras &&
      typeof window.Torras.updateCloneHeaderCartBadge === 'function'
    ) {
      window.Torras.updateCloneHeaderCartBadge(itemCount);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', () => {
        this.close('backdrop', 'cancel');
      });
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const sectionElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return sectionElement?.innerHTML ?? null;
  }

  async fetchSections(sectionIds) {
    const sectionsUrl = `${window.location.pathname}?sections=${sectionIds.map(encodeURIComponent).join(',')}`;
    const response = await fetch(sectionsUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) throw new Error(`Cart sections request failed with status ${response.status}.`);

    const sections = await response.json();
    const hasMissingSection = sectionIds.some((sectionId) => !sections?.[sectionId]);
    if (hasMissingSection) throw new Error('Cart sections response is incomplete.');

    return sections;
  }

  redirectToCart() {
    window.location.assign(window.routes?.cart_url || '/cart');
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getCartItemCount(parsedState) {
    if (parsedState.item_count != null) return parsedState.item_count;

    const cartIconHTML = parsedState.sections?.['cart-icon-bubble'];
    if (!cartIconHTML) return null;

    const cartIconDocument = new DOMParser().parseFromString(cartIconHTML, 'text/html');
    const countBubble = cartIconDocument.querySelector('.cart-count-bubble');
    if (!countBubble) return 0;

    const visibleCount = countBubble.querySelector('[aria-hidden="true"]')?.textContent.trim();
    return visibleCount ? Number(visibleCount) : 100;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
